import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { map, tap } from 'rxjs';

import { GradeModel } from '@app/core/models/grade.model';
import { SchoolModel } from '@app/core/models/school.model';
import { MyWorldHttpService } from '@app/core/services/http/my-world-http.service';
import { ToastService } from '@app/core/ui/components/toast';
import { LoadingStateEnum } from '@app/shared/enums/loading-state.enum';
import { catchRemoteError } from '@app/shared/rxjs/custom-operators';

import { LoadStudentByNameAction } from './student-schools.action';

export interface StudentSchoolsStateModel {
  studentIds: number[];
  studentSchools: SchoolModel[];
  grades: GradeModel[];
  requestStatus: LoadingStateEnum;
}

const STUDENT_SCHOOLS_STATE_TOKEN = new StateToken<StudentSchoolsStateModel>(
  'studentSchools'
);
const initialState: Partial<StudentSchoolsStateModel> = {
  studentIds: [],
  studentSchools: [],
  grades: []
};

@State<StudentSchoolsStateModel>({
  name: STUDENT_SCHOOLS_STATE_TOKEN,
  defaults: {
    ...(initialState as StudentSchoolsStateModel),
    requestStatus: LoadingStateEnum.idle
  }
})
@Injectable()
export class StudentSchoolsState {
  constructor(
    private http: MyWorldHttpService,
    private toast: ToastService
  ) {}

  @Selector() static studentschool(studentSchool: StudentSchoolsStateModel) {
    return studentSchool;
  }
  @Selector() static schools({ studentSchools }: StudentSchoolsStateModel) {
    return studentSchools;
  }
  @Selector() static grades({ grades }: StudentSchoolsStateModel) {
    return grades;
  }

  @Selector() static studentIds({ studentIds }: StudentSchoolsStateModel) {
    return studentIds;
  }

  @Selector() static requestStatus({
    requestStatus
  }: StudentSchoolsStateModel) {
    return requestStatus;
  }

  @Selector() static isLoading({ requestStatus }: StudentSchoolsStateModel) {
    return requestStatus === LoadingStateEnum.loading;
  }

  @Action(LoadStudentByNameAction) loadStudentByName(
    { patchState }: StateContext<StudentSchoolsStateModel>,
    { payload }: LoadStudentByNameAction
  ) {
    patchState({ requestStatus: LoadingStateEnum.loading });
    return this.http.getStudentDataByName(payload.trim()).pipe(
      catchRemoteError(
        (remoteError) => {
          this.toast.danger(remoteError.message);
          patchState({
            ...initialState,
            requestStatus: LoadingStateEnum.error
          });
        },
        {
          defaultMessage:
            'Não foi possível encontrar o aluno. Por favor, tente novamente.'
        }
      ),
      map((data) => {
        return { ...data, grades: this.formatGrades(data.grades) };
      }),
      tap((data) => {
        patchState({ ...data, requestStatus: LoadingStateEnum.loaded });
      })
    );
  }

  private formatGrades(grades: GradeModel[]) {
    if (!grades?.length) {
      return [];
    }
    return grades
      .map((grade) => ({
        ...grade,
        name: grade.name
          .replace('Regular', '')
          .trim()
          .replace(/^(Infantil \d+)/, (match) => `${match} anos`)
      }))
      .sort((a, b) => {
        if (a.segment.name < b.segment.name) {
          return -1;
        }
        if (a.segment.name > b.segment.name) {
          return 1;
        }
        return a.name.localeCompare(b.name, 'pt', { numeric: true });
      });
  }
}
